import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { _get } from "../services/api.service";

export interface SubscriptionDetail {
  status: "active" | "paused" | "cancelled" | "expired";
  current_billing_start: string;
  current_billing_end: string;
  plan_name: string;
  price: string;
  currency: "INR" | "USD" | string;
  credit_balance: number;
  total_credit: number;
  ended_at: string | null;
  cancel_requested: boolean;
  cancel_requested_at: string;
}

export type UserProfile = {
  gender: string | null;
  default_voice: any;
  profile_picture: string | null;
  instagram_handle: string | null;
  closing_line: string | null;
  is_first_login: boolean;
  guide_video_url: string | null;
  free_credit_balance: number;
  total_free_credits: number;
};

export type MeResponse = {
  id: number;
  email: string;
  phone: string | null;
  first_name: string;
  last_name: string;
  last_login: string;
  date_joined: string;
  profile: UserProfile;
  has_subscription: boolean;
  subscription_id: number | null;
};

export interface CreditInfo {
  free: {
    total: number;
    balance: number;
  };
  paid: {
    total: number;
    balance: number;
  };
  combined: {
    total: number;
    balance: number;
  };
}

type Subscription = SubscriptionDetail & {
  active: boolean;
  subscription_id: number;
};

type MainApiContextType = {
  me: MeResponse | null;
  subscription: Subscription | null;
  credits: CreditInfo | null;
  hasSubscription: boolean;
  loading: boolean;
  refresh: (options?: { force?: boolean }) => Promise<void>;
};

const MainApiContext = createContext<MainApiContextType | null>(null);

export const MainApiProvider = ({ children }: { children: ReactNode }) => {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [credits, setCredits] = useState<CreditInfo | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loading, setLoading] = useState(false);

  const initialized = useRef(false);
  const fetchInProgressRef = useRef<Promise<void> | null>(null);

  const fetchApis = async (options?: { force?: boolean }) => {
    const force = options?.force === true;

    if (fetchInProgressRef.current && !force) {
      return fetchInProgressRef.current;
    }

    fetchInProgressRef.current = (async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem("TOKEN");
        if (!token) return;

        const meRes: MeResponse = await _get("users/accounts/me");
        setMe(meRes);

        const freeTotal = meRes.profile?.total_free_credits ?? 0;
        const freeBalance = meRes.profile?.free_credit_balance ?? 0;

        if (!meRes.has_subscription || !meRes.subscription_id) {
          setHasSubscription(false);
          setSubscription(null);
          setCredits({
            free: { total: freeTotal, balance: freeBalance },
            paid: { total: 0, balance: 0 },
            combined: {
              total: freeTotal,
              balance: freeBalance,
            },
          });
          return;
        }

        setHasSubscription(true);

        const subRes: SubscriptionDetail = await _get(
          `subscriptions/detail/${meRes.subscription_id}/`
        );

        const paidTotal = subRes.total_credit ?? 0;
        const paidBalance = subRes.credit_balance ?? 0;

        setSubscription({
          ...subRes,
          active: true,
          subscription_id: meRes.subscription_id,
        });

        setCredits({
          free: { total: freeTotal, balance: freeBalance },
          paid: { total: paidTotal, balance: paidBalance },
          combined: {
            total: freeTotal + paidTotal,
            balance: freeBalance + paidBalance,
          },
        });
      } catch (error) {
        console.error("Main API error", error);
        throw error;
      } finally {
        setLoading(false);
        fetchInProgressRef.current = null;
      }
    })();

    return fetchInProgressRef.current;
  };

  useEffect(() => {
    if (!initialized.current) {
      fetchApis();
      initialized.current = true;
    }
  }, []);

  return (
    <MainApiContext.Provider
      value={{
        me,
        subscription,
        credits,
        hasSubscription,
        loading,
        refresh: fetchApis,
      }}
    >
      {children}
    </MainApiContext.Provider>
  );
};

export const useMainApi = () => {
  const ctx = useContext(MainApiContext);
  if (!ctx) {
    throw new Error("useMainApi must be used inside MainApiProvider");
  }
  return ctx;
};
